import { Users, FileText, Zap, Layers, ShieldCheck, UploadCloud, MessageSquare } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";

const Home = ({ onNavigate }) => {
  const { logout, user } = useAuth();

  return (
    <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 overflow-y-auto relative transition-colors duration-300">
      {/* TOP RIGHT USER INFO & LOGOUT BUTTON - Fixed Position */}
      {/* TOP RIGHT USER INFO & LOGOUT BUTTON - REMOVED (Moved to Navbar) */}

      {/* Main Content */}
      <div className="p-4 sm:p-6 md:p-8 pt-20">
        <div className="max-w-7xl mx-auto">

          {/* ================= TITLE ================= */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8 sm:mb-12 md:mb-16 px-2"
          >
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Your AI-Powered Recruitment Hub
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Intelligent resume filtering, role management, and automated HR workflows — in one place.
            </p>
          </motion.div>

          {/* ================= AGENT CARDS ================= */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full max-w-4xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 place-items-center"
          >
            {/* Recruiter Agent */}
            <AnimatedCard onClick={() => onNavigate("recruiter")}>
              <Users className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 text-orange-600 mb-4 sm:mb-6" />
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-2">Recruiter Agent</h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                AI-driven resume filtering, ranking, and hiring recommendations.
              </p>
            </AnimatedCard>

            {/* Inventory Agent */}
            <AnimatedCard onClick={() => onNavigate("inventory")}>
              <FileText className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 text-orange-600 mb-4 sm:mb-6" />
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-2">Inventory Agent</h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                Track and manage equipment issued to employees with intelligent logging.
              </p>
            </AnimatedCard>

            {/* Feedback Agent */}
            <AnimatedCard onClick={() => onNavigate("feedback")}>
              <MessageSquare className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 text-orange-600 mb-4 sm:mb-6" />
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-2">Feedback Agent</h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                Get AI-powered structured feedback on interview transcripts.
              </p>
            </AnimatedCard>
          </motion.div>

          {/* ================= FEATURES SECTION ================= */}
          <div className="mt-12 sm:mt-16 md:mt-20 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 shadow-xl border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-900 dark:text-white mb-6 sm:mb-8 md:mb-10 text-center">
              Powerful Features for Smarter HR
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 md:gap-10 max-w-4xl mx-auto">
              <Feature
                icon={<Zap className="text-orange-600" size={24} />}
                title="AI-Powered Resume Filtering"
                desc="Instantly evaluate, score, and shortlist the best candidates."
              />

              <Feature
                icon={<Layers className="text-orange-600" size={24} />}
                title="Dynamic Role Management"
                desc="Add, delete, and organize job roles with one click."
              />

              <Feature
                icon={<UploadCloud className="text-orange-600" size={24} />}
                title="Bulk Resume Uploads"
                desc="Effortlessly upload multiple PDFs at once with automated parsing."
              />

              <Feature
                icon={<ShieldCheck className="text-orange-600" size={24} />}
                title="Secure Cloud Storage"
                desc="All resumes stored safely using encrypted AWS S3 storage."
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;

/* ======================= REUSABLE COMPONENTS ======================= */

const AnimatedCard = ({ children, onClick }) => {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.04, y: -4 }}
      whileTap={{ scale: 0.98 }}
      className="
        bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl 
        rounded-2xl p-5 sm:p-6 md:p-8 lg:p-10 shadow-md border border-gray-200 dark:border-gray-700
        hover:shadow-xl text-left transition-all
        w-full max-w-sm
      "
    >
      {children}
    </motion.button>
  );
};

const Feature = ({ icon, title, desc }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="flex items-start gap-4"
    >
      <div className="w-12 h-12 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-1 text-lg">{title}</h4>
        <p className="text-gray-600 dark:text-gray-300 text-sm">{desc}</p>
      </div>
    </motion.div>
  );
};
